<template>
  <div class="is-default-theme">
    <div
      v-if="hasIntroAnimationStarted"
      class="clara-bubble-glow is-circular has-pointer-events-none"
    />

    <div
      class="clara-for-planners-bubble"
      :style="bubbleStyle"
      :data-e2e="AICypressAttribute.views.CLARA_FOR_PLANNERS_BUBBLE"
    >
      <div
        class="clara-launcher-drag-area is-flex has-user-select-none"
        :data-e2e="AICypressAttribute.views.CLARA_FOR_PLANNERS_LAUNCHER_DRAG_AREA"
        @pointerdown="onDragStart"
      >
        <ClaraForPlannersLauncher
          v-show="!isOpen"
          :is-animating="isIntroAnimating"
          @open="onOpen"
        />
      </div>
      <!-- The panel root uses Bulma's is-flex (display: flex !important), which overrides v-show. -->
      <div v-if="hasBeenOpened" v-show="isOpen">
        <ClaraForPlannersPanel :conversation="conversation" :session-id="sessionId" @minimize="onMinimize" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import ClaraForPlannersLauncher from '@/modules/ai/components/presentational/clara-for-planners/ClaraForPlannersLauncher.vue';
import ClaraForPlannersPanel from '@/modules/ai/components/stateful/clara-for-planners/ClaraForPlannersPanel.vue';
import { ClaraConversationObject } from '@/modules/ai/models/ClaraConversationObject';
import { ClaraForPlannersChatOpenedEventTracking } from '@/modules/ai/models/segmentevents/ClaraForPlannersChatOpenedEventTracking';
import { ClaraForPlannersChatClosedEventTracking } from '@/modules/ai/models/segmentevents/ClaraForPlannersChatClosedEventTracking';
import ClaraService from '@/modules/ai/services/ClaraService';
import ClaraForPlannersLauncherPositionService from '@/modules/ai/services/ClaraForPlannersLauncherPositionService';
import { AICypressAttribute } from '@/modules/ai/constants/AICypressAttribute';
import { ClaraForPlannersLauncherPosition } from '@/modules/ai/interfaces/ClaraForPlannersLauncherPosition';
import { commits, getters } from '@/modules/core/app/helpers/store';
import DocumentUIService from '@/modules/core/app/services/DocumentUIService';
import { defineComponent } from 'vue';
import { v4 as uuidv4 } from 'uuid';

const INTRO_ANIMATION_DURATION_MS: number = 8000;
const DEFAULT_POSITION_OFFSET_PX: number = 24;
const LAUNCHER_SIZE_PX: number = 48;
const DRAG_THRESHOLD_PX: number = 4;

export default defineComponent({
  name: 'ClaraForPlannersBubble',
  components: {
    ClaraForPlannersLauncher,
    ClaraForPlannersPanel
  },
  static: {
    AICypressAttribute
  },
  data(): {
    isOpen: boolean;
    hasBeenOpened: boolean;
    hasIntroAnimationStarted: boolean;
    isIntroAnimating: boolean;
    introAnimationTimer: ReturnType<typeof setTimeout> | null;
    conversation: ClaraConversationObject;
    sessionId: string;
    openedAt: number;
    position: ClaraForPlannersLauncherPosition;
    dragOrigin: { x: number; y: number; right: number; bottom: number } | null;
    hasDragged: boolean;
  } {
    return {
      isOpen: false,
      hasBeenOpened: false,
      hasIntroAnimationStarted: false,
      isIntroAnimating: false,
      introAnimationTimer: null,
      conversation: ClaraConversationObject.factory({ messages: [] }),
      sessionId: uuidv4(),
      openedAt: 0,
      position: { right: DEFAULT_POSITION_OFFSET_PX, bottom: DEFAULT_POSITION_OFFSET_PX },
      dragOrigin: null,
      hasDragged: false
    };
  },
  computed: {
    hasViewedLauncher(): boolean {
      return !!getters.auth.getUser()?.hasViewedClaraForPlannersLauncher;
    },
    bubbleStyle(): Record<string, string> {
      return {
        right: `${this.position.right}px`,
        bottom: `${this.position.bottom}px`
      };
    }
  },
  methods: {
    onOpen(): void {
      if (this.hasDragged) {
        this.hasDragged = false;
        return;
      }

      this.isOpen = true;
      this.hasBeenOpened = true;
      this.openedAt = Date.now();
      ClaraForPlannersChatOpenedEventTracking.dispatch(getters.auth.getUser(), this.sessionId);
    },
    onMinimize(): void {
      this.isOpen = false;
      this.trackChatClosed();
    },
    trackChatClosed(): void {
      ClaraForPlannersChatClosedEventTracking.dispatch(
        getters.auth.getUser(),
        this.sessionId,
        this.openedAt
      );
    },
    playIntroAnimation(): void {
      this.hasIntroAnimationStarted = true;
      this.isIntroAnimating = true;
      // Keeps a remount in this session from replaying the intro.
      commits.auth.setHasViewedClaraForPlannersLauncher();
      // A failed write only replays the intro on the next login.
      ClaraService.setHasViewedClaraForPlannersLauncher().catch(() => {});

      this.introAnimationTimer = setTimeout(() => {
        this.isIntroAnimating = false;
      }, INTRO_ANIMATION_DURATION_MS);
    },
    onDragStart(event: PointerEvent): void {
      if (event.button !== 0) {
        return;
      }

      this.hasDragged = false;
      this.dragOrigin = {
        x: event.clientX,
        y: event.clientY,
        right: this.position.right,
        bottom: this.position.bottom
      };

      DocumentUIService.addEventListener('pointermove', this.onDragMove);
      DocumentUIService.addEventListener('pointerup', this.onDragEnd);
      DocumentUIService.addEventListener('pointercancel', this.onDragEnd);
    },
    onDragMove(event: PointerEvent): void {
      if (!this.dragOrigin) {
        return;
      }

      const deltaX: number = this.dragOrigin.x - event.clientX;
      const deltaY: number = this.dragOrigin.y - event.clientY;

      if (!this.hasDragged && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD_PX) {
        return;
      }

      this.hasDragged = true;
      this.position = this.clampPosition({
        right: this.dragOrigin.right + deltaX,
        bottom: this.dragOrigin.bottom + deltaY
      });
    },
    onDragEnd(): void {
      this.removeDragListeners();

      if (this.hasDragged) {
        ClaraForPlannersLauncherPositionService.save(this.position);
      }

      this.dragOrigin = null;
    },
    removeDragListeners(): void {
      DocumentUIService.removeEventListener('pointermove', this.onDragMove);
      DocumentUIService.removeEventListener('pointerup', this.onDragEnd);
      DocumentUIService.removeEventListener('pointercancel', this.onDragEnd);
    },
    onViewportResize(): void {
      this.position = this.clampPosition(this.position);
    },
    clampPosition(position: ClaraForPlannersLauncherPosition): ClaraForPlannersLauncherPosition {
      return {
        right: this.clamp(
          position.right,
          0,
          DocumentUIService.getViewportWidth() - LAUNCHER_SIZE_PX
        ),
        bottom: this.clamp(
          position.bottom,
          0,
          DocumentUIService.getViewportHeight() - LAUNCHER_SIZE_PX
        )
      };
    },
    clamp(value: number, min: number, max: number): number {
      return Math.min(Math.max(value, min), max);
    }
  },
  created(): void {
    const storedPosition: ClaraForPlannersLauncherPosition | null =
      ClaraForPlannersLauncherPositionService.load();

    if (storedPosition) {
      this.position = this.clampPosition(storedPosition);
    }
  },
  mounted(): void {
    DocumentUIService.addEventListener('resize', this.onViewportResize);

    if (this.hasViewedLauncher) {
      return;
    }

    this.playIntroAnimation();
  },
  beforeUnmount(): void {
    this.removeDragListeners();
    DocumentUIService.removeEventListener('resize', this.onViewportResize);

    if (this.introAnimationTimer) {
      clearTimeout(this.introAnimationTimer);
    }

    if (this.isOpen) {
      this.trackChatClosed();
    }
  }
});
</script>

<style scoped>
.clara-for-planners-bubble {
  position: fixed;
  z-index: var(--ce-z-index-clara-bubble);
}

.clara-launcher-drag-area {
  touch-action: none;
}

.clara-bubble-glow {
  position: fixed;
  z-index: var(--ce-z-index-clara-glow);
  right: -260px;
  bottom: -200px;
  width: 795px;
  height: 794px;
  background: linear-gradient(322deg, var(--ce-primary) 16.94%, rgba(255, 134, 91, 0.1) 87%);
  filter: blur(60px);
  opacity: 0;
  transform-origin: 78% 78%;
  animation: clara-glow-collapse 8s linear forwards;
}

@keyframes clara-glow-collapse {
  0% {
    opacity: 0;
    transform: scale(1);
  }
  3.75% {
    opacity: 0;
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
  }
  12.5% {
    opacity: 0.3;
    transform: scale(1);
  }
  40% {
    opacity: 0.25;
    transform: scale(1);
    animation-timing-function: cubic-bezier(0.5, 0, 0.5, 1);
  }
  72.5% {
    opacity: 0;
    transform: scale(0.06);
  }
  100% {
    opacity: 0;
    transform: scale(0.06);
  }
}
</style>
